import {
  CheckableTagSelectItem,
  FlexBox,
  Select,
  TagSelectItem,
  TagSelectSuggestion,
} from '@dixa/lib-shared-habla';
import type { ReadSearchApiArg } from 'lib-search-types';
import { isResponseWithData } from 'lib-shared-auth';
import { ampli } from 'lib-shared-tracking';
import { Tag } from 'lib-shared-types-api';
import { PropsWithClassName } from 'lib-shared-types-utils';
import { useCallback, useMemo, useState } from 'react';
import { useUpdateEffect } from 'react-use';

import { useLazyReadSearchInfiniteQuery } from 'apis/kraken';
import { tagsApi } from 'apis/tags';
import { stringToHexColor } from 'utils/color/color';

import styles from './tagSelect.module.scss';

type TagSelectProps = {
  /** The selected tags. */
  selectedTags?: Tag[];
  /** Whether to include deactivated tags in the list. */
  includeDeactivated?: boolean;
  /** Whether to allow selecting deactivated tags. */
  allowSelectDeactivated?: boolean;
  /** Whether to allow creating new tags. */
  allowTagCreation?: boolean;
  /** Whether to allow multiple selection. */
  allowMultiple?: boolean;
  /** The placeholder text. */
  placeholder?: string;
  /** The callback for when tag selection is changed. */
  onChange?: (tags: readonly Tag[]) => void;
} & PropsWithClassName;

/** Represents the component for selecting one or more tags. */
export const TagSelect = ({
  className,
  selectedTags = [],
  includeDeactivated,
  allowSelectDeactivated,
  allowMultiple,
  allowTagCreation,
  placeholder,
  onChange,
}: TagSelectProps): JSX.Element => {
  const [searchTerm, setSearchTerm] = useState<string>();

  const initialArg: ReadSearchApiArg<'tags'> = useMemo(
    () => ({
      scope: 'tags',
      sortBy: { field: 'name', order: 'asc' },
      ...(!includeDeactivated && {
        filters: {
          strategy: 'all',
          filters: [
            {
              field: 'is_deactivated',
              operator: 'not_equals',
              values: [{ value: 'true' }],
            },
          ],
        },
      }),
    }),
    [includeDeactivated],
  );

  const [
    readSearchTags,
    {
      results: tags,
      isFetching,
      hasNextPage,
      hasPreviousPage,
      fetchNextPage,
      fetchPreviousPage,
    },
  ] = useLazyReadSearchInfiniteQuery<'tags'>();

  useUpdateEffect(() => {
    readSearchTags(
      {
        ...initialArg,
        query: searchTerm,
      },
      { debounceTime: 300 },
    );
  }, [readSearchTags, initialArg, searchTerm]);

  const [createTag] = tagsApi.useCreateTagMutation();
  const disabledTags = useMemo(
    () =>
      includeDeactivated && !allowSelectDeactivated
        ? tags.filter((tag) => tag.is_deactivated).map((tag) => tag.id)
        : [],
    [allowSelectDeactivated, includeDeactivated, tags],
  );
  const doesTagNameExist = useMemo(
    () =>
      tags.some((tag) => tag.name.toLowerCase() === searchTerm?.toLowerCase()),
    [searchTerm, tags],
  );
  const shouldShowSuggestion =
    allowTagCreation &&
    !doesTagNameExist &&
    (searchTerm?.trim().length ?? 0) > 1;

  /** Handles click on new tag suggestion. */
  const handleSuggest = useCallback(
    async (name: string) => {
      ampli.tagSelectCreateNewTagClicked({
        tagName: name,
        component: 'TagSelect',
      });

      const response = await createTag({ name, color: stringToHexColor(name) });

      if (isResponseWithData(response)) {
        onChange?.([...selectedTags, response.data.data]);
      }
    },
    [createTag, selectedTags, onChange],
  );

  /** Handles searching the tags. */
  const handleSearch = useCallback((value: string) => {
    setSearchTerm(value.trim() || undefined);
  }, []);

  /** Handles the change of selection. */
  const handleChange = useCallback(
    (tag: Tag, checked: boolean): void => {
      const nextTags = checked
        ? [...selectedTags, tag]
        : selectedTags.filter(({ id }) => id !== tag.id);

      onChange?.(nextTags);
    },
    [selectedTags, onChange],
  );

  /** Handles toggling the dropdown of the select component. */
  const handleToggle = useCallback(
    (isOpen: boolean) => {
      if (isOpen && !tags.length) {
        readSearchTags({
          ...initialArg,
          query: searchTerm,
        });
      }

      if (!isOpen) {
        setSearchTerm(undefined);
      }
    },
    [readSearchTags, initialArg, searchTerm, tags.length],
  );

  /** Takes search string and returns suggestion item content */
  const renderSuggestion = useCallback(
    (search) => (
      <FlexBox gap="300" alignItems="center">
        <div
          className={styles.tagColor}
          style={{ backgroundColor: stringToHexColor(search) }}
        />
        <div>Create &quot;{search}&quot; tag</div>
      </FlexBox>
    ),
    [],
  );

  return (
    <Select
      className={className}
      multiple={allowMultiple}
      searchable
      emptyMessage={searchTerm ? 'No tags found' : 'No tags available'}
      items={tags as Tag[]}
      selectedItems={selectedTags as Tag[]}
      disabledItems={disabledTags}
      itemComponent={allowMultiple ? CheckableTagSelectItem : TagSelectItem}
      loading={isFetching}
      onSearch={handleSearch}
      onToggle={handleToggle}
      onChange={handleChange}
      pagination={{
        infinite: true,
        nextEnable: hasNextPage,
        previousEnable: hasPreviousPage,
        onNext: () => Promise.resolve(fetchNextPage()),
        onPrevious: () => Promise.resolve(fetchPreviousPage()),
      }}
      placeholder={
        placeholder ??
        (allowMultiple ? 'Select one or more tags' : 'Select a tag')
      }
      onSuggest={handleSuggest}
      suggestionComponent={
        shouldShowSuggestion ? TagSelectSuggestion : undefined
      }
      renderSuggestion={renderSuggestion}
    />
  );
};
